import { RaidPlanDeathPull } from 'app/frontend/base/api/raid_plans'
import { heatmapPhaseKey } from './castHeatmap'

export const DEATH_BUCKET_SECONDS = 8
export const DEFAULT_DEATH_LIMIT = 4
export const MAX_DEATH_LIMIT = 30

export type DeathMarker = {
  at: number
  count: number
  pullCount: number
  opacity: number
  strokeWidth: number
}

const phaseIndexFor = (phaseId: string, phases: Array<{ id: string }>) => {
  const key = heatmapPhaseKey(phaseId)
  return phases.findIndex((phase) => heatmapPhaseKey(phase.id) === key)
}

export const buildDeathMarkers = ({
  pulls,
  phases,
  phaseStarts,
  effectivePhaseDurations,
  limit,
  bucketSeconds = DEATH_BUCKET_SECONDS,
}: {
  pulls: RaidPlanDeathPull[]
  phases: Array<{ id: string }>
  phaseStarts: number[]
  effectivePhaseDurations: number[]
  limit: number
  bucketSeconds?: number
}): DeathMarker[] => {
  const buckets = new Map<number, { count: number; pullCount: number }>()

  pulls.forEach((pull) => {
    const seenBuckets = new Set<number>()
    pull.slice(0, limit).forEach(([phaseId, offset]) => {
      const phaseIndex = phaseIndexFor(phaseId, phases)
      if (phaseIndex < 0) return
      const phaseDuration = effectivePhaseDurations[phaseIndex] ?? 0
      if (offset < 0 || offset >= phaseDuration) return

      const at = (phaseStarts[phaseIndex] ?? 0) + offset
      const bucket = Math.floor(at / bucketSeconds) * bucketSeconds
      const entry = buckets.get(bucket) ?? { count: 0, pullCount: 0 }
      entry.count += 1
      if (!seenBuckets.has(bucket)) {
        seenBuckets.add(bucket)
        entry.pullCount += 1
      }
      buckets.set(bucket, entry)
    })
  })

  const peak = Math.max(...[...buckets.values()].map((entry) => entry.count), 0)
  if (peak <= 0) return []

  return [...buckets.entries()]
    .sort((left, right) => left[0] - right[0])
    .map(([bucket, entry]) => {
      const emphasis = Math.sqrt(entry.count / peak)
      const thickness = peak <= 1 ? 0 : (entry.count - 1) / (peak - 1)
      return {
        at: bucket + bucketSeconds / 2,
        count: entry.count,
        pullCount: entry.pullCount,
        opacity: 0.4 + 0.6 * emphasis,
        strokeWidth: 1 + 5 * thickness,
      }
    })
}
